
import { useToast } from "@/hooks/use-toast";
import { RecipeIdea } from "../types";
import { db } from "@/integrations/cloudflare/client";

export const useRecipeOperations = (
  recipes: RecipeIdea[],
  setRecipes: (recipes: RecipeIdea[]) => void
) => {
  const { toast } = useToast();

  // Handle deleting a recipe
  const handleDeleteRecipe = async (id: string) => {
    if (window.confirm("Are you sure you want to delete this recipe?")) {
      try {
        const { error } = await db
          .from('holiday_recipes')
          .delete()
          .eq('id', id);
        
        if (error) {
          throw error;
        }
        
        // Remove from local state
        setRecipes(recipes.filter(recipe => recipe.id !== id));
        
        toast({
          title: "Success",
          description: "Recipe deleted successfully",
        });
      } catch (error) {
        console.error('Error deleting recipe:', error);
        toast({
          title: "Error",
          description: "Failed to delete recipe",
          variant: "destructive",
        });
      }
    }
  };
  
  // Handle saving a recipe (create or update)
  const handleSaveRecipe = async (
    recipeData: Omit<RecipeIdea, 'id' | 'createdAt' | 'updatedAt'>,
    selectedRecipe: RecipeIdea | null
  ) => {
    try {
      if (selectedRecipe) {
        // Update existing recipe
        const { error } = await db
          .from('holiday_recipes')
          .update({
            title: recipeData.title,
            description: recipeData.description,
            ingredients: recipeData.ingredients,
            instructions: recipeData.instructions,
            prep_time: recipeData.prepTime,
            cook_time: recipeData.cookTime,
            total_time: recipeData.totalTime,
            servings: recipeData.servings,
            difficulty: recipeData.difficulty,
            category: recipeData.category,
            tags: recipeData.tags,
            image_url: recipeData.imageUrl,
            kid_friendly: recipeData.kidFriendly,
            updated_at: new Date().toISOString()
          })
          .eq('id', selectedRecipe.id);
        
        if (error) {
          throw error;
        }
        
        // Update local state
        setRecipes(recipes.map(recipe => 
          recipe.id === selectedRecipe.id 
            ? { 
                ...recipe, 
                ...recipeData, 
                updatedAt: new Date().toISOString() 
              } 
            : recipe
        ));
        
        toast({
          title: "Success",
          description: "Recipe updated successfully",
        });
        
        return true;
      } else {
        // Create new recipe
        const { data, error } = await db
          .from('holiday_recipes')
          .insert({
            title: recipeData.title,
            description: recipeData.description,
            ingredients: recipeData.ingredients,
            instructions: recipeData.instructions,
            prep_time: recipeData.prepTime,
            cook_time: recipeData.cookTime,
            total_time: recipeData.totalTime,
            servings: recipeData.servings,
            difficulty: recipeData.difficulty,
            category: recipeData.category,
            tags: recipeData.tags,
            image_url: recipeData.imageUrl,
            kid_friendly: recipeData.kidFriendly,
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString()
          })
          .select();
        
        if (error) {
          throw error;
        }
        
        if (data && data[0]) {
          // Add to local state with the newly created ID
          const newRecipe: RecipeIdea = {
            ...recipeData,
            id: data[0].id,
            createdAt: data[0].created_at,
            updatedAt: data[0].updated_at
          };
          
          setRecipes([...recipes, newRecipe]);
        }
        
        toast({
          title: "Success",
          description: "Recipe created successfully",
        });
        
        return true;
      }
    } catch (error) {
      console.error('Error saving recipe:', error);
      toast({
        title: "Error",
        description: "Failed to save recipe",
        variant: "destructive",
      });
      return false;
    }
  };

  return {
    handleDeleteRecipe,
    handleSaveRecipe,
  };
};
